/**
 * useBreadcrumb — Route-aware breadcrumb hook
 * Returns breadcrumb segments with labels, hrefs, and loading states.
 */
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface BreadcrumbSegment {
  label: string;
  href?: string;
  isLoading?: boolean;
}

const STATIC_ROUTES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/dashboard/dealflow": "Dealflow",
  "/dashboard/sequences": "Sequences",
  "/sequences": "Sequences",
  "/brands": "Brands",
  "/dashboard/scans": "Scans",
  "/dashboard/contacts": "Contacts",
  "/dashboard/saved": "Content",
  "/dashboard/insights": "Insights",
  "/dashboard/reports": "Reports",
  "/dashboard/settings": "Settings",
  "/dashboard/mentions": "Mentions",
  "/dashboard/mentioners": "Mentioners",
  "/dashboard/embed": "Embed",
  "/dashboard/team": "Team",
  "/dashboard/my-team": "My Team",
  "/dashboard/portals": "Client Portals",
  "/dashboard/monthly-reports": "Monthly Reports",
  "/dashboard/custom-roles": "Custom Roles",
  "/dashboard/audit-logs": "Audit Logs",
  "/dashboard/brand-kits": "Brand Kits",
  "/dashboard/blog-generator": "Blog Generator",
  "/dashboard/clips": "Clips",
  "/dashboard/slider-builder": "Slider Builder",
  "/dashboard/comparison-card": "Comparison Card",
  "/dashboard/trend-chart": "Trend Chart",
  "/dashboard/competitors": "Competitors",
  "/dashboard/manager": "Manager View",
  "/arsenal": "My Arsenal",
  "/executive": "Executive View",
  "/help": "Help Center",
  "/settings/profile": "My Profile",
  "/settings/company-profile": "Company Profile",
  "/owner": "Owner Panel",
  "/owner/health": "Health Dashboard",
  "/owner/filtering-analytics": "Filtering Analytics",
};

const TOOL_NAMES: Record<string, string> = {
  transcription: "Audio Transcription",
  sentiment: "Sentiment Analyzer",
  "logo-detection": "Logo Detector",
  "mention-counter": "Mention Counter",
  "clip-generator": "Clip Generator",
  translate: "Translator",
  "missed-demand": "Missed Demand Brief",
  "objection-brief": "Objection Brief",
  "vocabulary-map": "Vocabulary Map",
  "frustration-map": "Frustration Map",
  "switching-signals": "Switching Signals",
  "buying-triggers": "Buying Triggers",
};

export function useBreadcrumb(): BreadcrumbSegment[] {
  const location = useLocation();
  const path = location.pathname;
  const [dynamicName, setDynamicName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Check for dynamic segments that need resolution
  useEffect(() => {
    setDynamicName(null);
    const brandsMatch = path.match(/^\/brands\/([a-f0-9-]+)$/i);
    const dealflowMatch = path.match(/^\/dashboard\/dealflow\/([a-f0-9-]+)$/i);
    const scansMatch = path.match(/^\/dashboard\/scans\/([a-f0-9-]+)$/i);
    const reportsMatch = path.match(/^\/dashboard\/reports\/([a-f0-9-]+)$/i);
    const clipsMatch = path.match(/^\/dashboard\/clips\/([a-f0-9-]+)$/i);

    const fetchName = async () => {
      setLoading(true);
      try {
        if (brandsMatch) {
          const { data } = await supabase.from("brands").select("brand_name").eq("id", brandsMatch[1]).single();
          setDynamicName(data?.brand_name || "[brand]");
        } else if (dealflowMatch) {
          const { data } = await supabase.from("dealflow").select("contact_company, contact_name").eq("id", dealflowMatch[1]).single();
          setDynamicName(data?.contact_company || data?.contact_name || "[lead]");
        } else if (scansMatch) {
          const { data } = await supabase.from("scan_history").select("primary_term").eq("id", scansMatch[1]).single();
          setDynamicName(data?.primary_term || "[scan]");
        } else if (reportsMatch) {
          const { data } = await supabase.from("reports").select("title").eq("id", reportsMatch[1]).single();
          setDynamicName(data?.title || "[report]");
        } else if (clipsMatch) {
          setDynamicName("[clip]");
        }
      } catch {
        setDynamicName("[item]");
      } finally {
        setLoading(false);
      }
    };

    if (brandsMatch || dealflowMatch || scansMatch || reportsMatch || clipsMatch) {
      fetchName();
    }
  }, [path]);

  // Static route
  if (STATIC_ROUTES[path]) {
    return [{ label: STATIC_ROUTES[path] }];
  }

  // Tools routes
  if (path === "/tools" || path === "/free-tools") {
    return [{ label: "Free Tools" }];
  }
  const toolMatch = path.match(/^\/tools\/(.+)$/);
  if (toolMatch) {
    const toolName = TOOL_NAMES[toolMatch[1]] || toolMatch[1];
    return [
      { label: "Free Tools", href: "/tools" },
      { label: toolName },
    ];
  }

  // Settings with tab
  if (path.startsWith("/dashboard/settings")) {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab");
    if (tab) {
      return [
        { label: "Settings", href: "/dashboard/settings" },
        { label: tab.charAt(0).toUpperCase() + tab.slice(1).replace(/-/g, " ") },
      ];
    }
    return [{ label: "Settings" }];
  }

  // Dynamic routes with resolved names
  const brandsMatch = path.match(/^\/brands\/([a-f0-9-]+)$/i);
  if (brandsMatch) {
    return [
      { label: "Brands", href: "/brands" },
      { label: loading ? "..." : (dynamicName || "..."), isLoading: loading },
    ];
  }

  const dealflowMatch = path.match(/^\/dashboard\/dealflow\/([a-f0-9-]+)$/i);
  if (dealflowMatch) {
    return [
      { label: "Dealflow", href: "/dashboard/dealflow" },
      { label: loading ? "..." : (dynamicName || "..."), isLoading: loading },
    ];
  }

  const scansMatch = path.match(/^\/dashboard\/scans\/([a-f0-9-]+)$/i);
  if (scansMatch) {
    return [
      { label: "Scans", href: "/dashboard/scans" },
      { label: loading ? "..." : (dynamicName || "..."), isLoading: loading },
    ];
  }

  const reportsMatch = path.match(/^\/dashboard\/reports\/([a-f0-9-]+)$/i);
  if (reportsMatch) {
    return [
      { label: "Reports", href: "/dashboard/reports" },
      { label: loading ? "..." : (dynamicName || "..."), isLoading: loading },
    ];
  }

  const clipsMatch = path.match(/^\/dashboard\/clips\/([a-f0-9-]+)$/i);
  if (clipsMatch) {
    return [
      { label: "Clips", href: "/dashboard/clips" },
      { label: loading ? "..." : (dynamicName || "..."), isLoading: loading },
    ];
  }

  // Help sub-routes
  if (path.startsWith("/help/")) {
    const segments = path.split("/").filter(Boolean);
    return [
      { label: "Help Center", href: "/help" },
      { label: segments.slice(1).join(" › ") },
    ];
  }

  // T789: Claims Check report viewer (never show the job id).
  if (/^\/claims-check\/job\/[^/]+$/i.test(path)) {
    return [
      { label: "Claims Check", href: "/claims-check" },
      { label: "Report" },
    ];
  }
  if (path === "/claims-check") return [{ label: "Claims Check" }];

  // Fallback. T789: a raw id is never a label (camera-ready); use the segment
  // before it instead.
  const segs = path.split("/").filter(Boolean);
  const isId = (x: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(x) || /^[0-9a-f]{20,}$/i.test(x);
  const lastSegment = [...segs].reverse().find((x) => !isId(x) && x !== "job") || "Page";
  return [{ label: lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, " ") }];
}
